"use client";

import type {
  ColumnFiltersState,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableOptions,
  TableState,
  Updater,
  VisibilityState,
} from "@tanstack/react-table";

import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";

import type { ExtendedColumnSort } from "@/lib/data-table";

import { DEBOUNCE_MS, DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, THROTTLE_MS } from "@/lib/table";

import { useDebouncedCallback } from "./use-debounced-callback";

type UseDataTableProps<TData> = {
  initialState?: Omit<Partial<TableState>, "sorting"> & {
    sorting?: ExtendedColumnSort<TData>[];
  };
  debounceMs?: number;
  throttleMs?: number;
  enableAdvancedFilter?: boolean;
  enableRowSelection?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  externalPagination?: PaginationState;
  externalSorting?: SortingState;
  externalColumnFilters?: ColumnFiltersState;
  manualPagination?: boolean;
  manualSorting?: boolean;
  manualFiltering?: boolean;
} & Omit<
  TableOptions<TData>,
  | "state"
  | "pageCount"
  | "getCoreRowModel"
  | "manualFiltering"
  | "manualPagination"
  | "manualSorting"
  | "onPaginationChange"
  | "onSortingChange"
  | "onColumnFiltersChange"
> &
  Required<Pick<TableOptions<TData>, "pageCount">>;

export function useDataTable<TData>(props: UseDataTableProps<TData>) {
  const {
    columns,
    pageCount = -1,
    initialState,
    debounceMs = DEBOUNCE_MS,
    throttleMs = THROTTLE_MS,
    enableAdvancedFilter = false,
    enableRowSelection = false,
    onPaginationChange: onPaginationChangeCallback,
    onSortingChange: onSortingChangeCallback,
    onColumnFiltersChange: onColumnFiltersChangeCallback,
    externalPagination,
    externalSorting,
    externalColumnFilters,
    manualPagination = false,
    manualSorting = false,
    manualFiltering = false,
    ...tableProps
  } = props;

  // Internal state management
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: (initialState?.pagination?.pageIndex ?? DEFAULT_PAGE_INDEX) - 1,
    pageSize: initialState?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
  });

  const [internalSorting, setInternalSorting] = React.useState<SortingState>(
    initialState?.sorting ?? []
  );

  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>(
    initialState?.columnFilters ?? []
  );

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>(
    initialState?.rowSelection ?? {}
  );

  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    initialState?.columnVisibility ?? {}
  );

  // Use external state if provided, otherwise use internal state
  const pagination = externalPagination ?? internalPagination;
  const sorting = externalSorting ?? internalSorting;
  const columnFilters = externalColumnFilters ?? internalColumnFilters;

  const onPaginationChange = React.useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      const newPagination =
        typeof updaterOrValue === "function" ? updaterOrValue(pagination) : updaterOrValue;

      if (!externalPagination) {
        setInternalPagination(newPagination);
      }
      onPaginationChangeCallback?.(newPagination);
    },
    [pagination, externalPagination, onPaginationChangeCallback]
  );

  const onSortingChange = React.useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      const newSorting =
        typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue;

      if (!externalSorting) {
        setInternalSorting(newSorting);
      }
      onSortingChangeCallback?.(newSorting);
    },
    [sorting, externalSorting, onSortingChangeCallback]
  );

  const debouncedSetFilterValues = useDebouncedCallback((filters: ColumnFiltersState) => {
    onColumnFiltersChangeCallback?.(filters);
  }, debounceMs);

  const onColumnFiltersChange = React.useCallback(
    (updaterOrValue: Updater<ColumnFiltersState>) => {
      if (enableAdvancedFilter) return;

      const newFilters =
        typeof updaterOrValue === "function" ? updaterOrValue(columnFilters) : updaterOrValue;

      if (!externalColumnFilters) {
        setInternalColumnFilters(newFilters);
      }
      debouncedSetFilterValues(newFilters);
    },
    [columnFilters, externalColumnFilters, debouncedSetFilterValues, enableAdvancedFilter]
  );

  const table = useReactTable({
    ...tableProps,
    columns,
    initialState,
    pageCount,
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    defaultColumn: {
      ...tableProps.defaultColumn,
      enableColumnFilter: false,
    },
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    onPaginationChange,
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    manualPagination,
    manualSorting,
    manualFiltering,
  });

  return {
    table,
    debounceMs,
    throttleMs,
    // Expose internal setters for controlled usage
    setRowSelection,
    setColumnVisibility,
  };
}
